import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTime, formatSeconds, formatCurrency } from '../utils/helpers';
import { videoCode } from '../utils/videoCode';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../utils/i18n';
import LoadingSpinner from './LoadingSpinner';
import '../App.css'; // For status icon styles

const VideoRow = ({
  video,
  narrator = 'Won',
  isSelected = false,
  onToggleSelection = () => {},
  onAction = () => {},
}) => {
  const { isAdmin } = useAuth();
  // The row's own labels are already Thai, but the length/cost/date cells come
  // from helpers.js, which does translate. Subscribing here is what re-renders
  // this memoized row when the language changes.
  useLanguage();
  const getStatusClass = (status) => {
    switch (status) {
      case 'new': return 'status-new';
      case 'recorded': return 'status-recorded';
      case 'pending_paid': return 'status-pending-paid';
      case 'paid': return 'status-paid';
      default: return '';
    }
  };

  const [isUpdatingReviewed, setIsUpdatingReviewed] = useState(false);
  const [isUpdatingPaid, setIsUpdatingPaid] = useState(false);
  const [isUpdatingHidden, setIsUpdatingHidden] = useState(false);

  // Get narrator-specific fields
  const getIsReviewed = () => {
    if (!video.audio_description) return false;
    return narrator === 'Waan' ? video.audio_description.is_reviewed_waan : video.audio_description.is_reviewed;
  };

  const getIsPaid = () => {
    if (!video.audio_description) return false;
    return narrator === 'Waan' ? video.audio_description.is_paid_waan : video.audio_description.is_paid;
  };

  const getCost = () => {
    return narrator === 'Waan' ? video.cost_thb_waan : video.cost_thb;
  };

  const getS3Link = () => {
    if (!video.audio_description) return '';
    return narrator === 'Waan' ? video.audio_description.s3_link_waan : video.audio_description.s3_link;
  };

  const getNeedsMore = () => {
    if (!video.audio_description) return false;
    return narrator === 'Waan' ? video.audio_description.needs_more_waan : video.audio_description.needs_more;
  };

  const isReviewed = getIsReviewed();
  const isPaid = getIsPaid();
  const cost = getCost();
  const s3Link = getS3Link();
  const needsMore = getNeedsMore();

  // Run a parent-owned optimistic action, toggling this row's spinner around it.
  // The parent applies the optimistic patch and handles rollback/errors.
  const runAction = (actionType, setBusy) => async () => {
    setBusy(true);
    try {
      await onAction(video, actionType);
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr>
      <td style={{ width: '40px' }}>
        {isAdmin && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelection(video.id)}
            style={{ cursor: 'pointer' }}
          />
        )}
      </td>
      <td>
        <span className={`status-icon ${getStatusClass(video.status)}`}></span>
        <Link to={`/videos/${video.id}`}>{videoCode(video)}</Link>
        {needsMore && !isReviewed && !isPaid && (
          <span className="badge bg-warning text-dark ms-2" title="แอดมินขอให้อัดเพิ่ม">อัดเพิ่ม</span>
        )}
      </td>
      <td>{video.name}</td>
      <td>{formatSeconds(video.length_seconds)}</td>
      <td>{formatCurrency(cost)}</td>
      <td>{formatDateTime(video.created_at)}</td>
      <td>{video.audio_description ? (isPaid ? 'จ่ายแล้ว' : (isReviewed ? 'รอการจ่ายตัง' : (s3Link !== '' && !needsMore ? 'รอการตรวจ' : 'ยังไม่ได้ทำ'))) : 'ยังไม่ได้ทำ'}</td>
      <td>
        {/* Actions: mark as reviewed / mark as paid / hide — admin only. */}
        {isAdmin && (
        <div className="d-flex">
          <button
            className={`btn btn-sm btn-icon me-2 ${isReviewed ? 'btn-warning' : 'btn-success'}`}
            onClick={runAction('reviewed', setIsUpdatingReviewed)}
            disabled={!video.audio_description || isUpdatingReviewed}
            aria-label={isReviewed ? 'Unmark reviewed' : 'Mark as reviewed'}
            title={isReviewed ? 'Unmark reviewed' : 'Mark as reviewed'}
          >
            {isUpdatingReviewed ? <LoadingSpinner small /> : <span aria-hidden="true">✔️</span>}
            <span className="sr-only">{isReviewed ? 'Unmark reviewed' : 'Mark as reviewed'}</span>
          </button>

          <button
            className={`btn btn-sm btn-icon me-2 ${isPaid ? 'btn-danger' : 'btn-info'}`}
            onClick={runAction('paid', setIsUpdatingPaid)}
            disabled={!video.audio_description || isUpdatingPaid}
            aria-label={isPaid ? 'Unmark paid' : 'Mark as paid'}
            title={isPaid ? 'Unmark paid' : 'Mark as paid'}
          >
            {isUpdatingPaid ? <LoadingSpinner small /> : <span aria-hidden="true">💸</span>}
            <span className="sr-only">{isPaid ? 'Unmark paid' : 'Mark as paid'}</span>
          </button>

          <button
            className={`btn btn-sm btn-icon ${video.is_hidden ? 'btn-danger' : 'btn-secondary'}`}
            onClick={runAction('hidden', setIsUpdatingHidden)}
            disabled={isUpdatingHidden}
            aria-label={video.is_hidden ? 'Show video' : 'Hide video'}
            title={video.is_hidden ? 'Show video' : 'Hide video'}
          >
            {isUpdatingHidden ? <LoadingSpinner small /> : <span aria-hidden="true">{video.is_hidden ? '👁️' : '🚫'}</span>}
            <span className="sr-only">{video.is_hidden ? 'Show video' : 'Hide video'}</span>
          </button>
        </div>
        )}
      </td>
    </tr>
  );
};

// Memoized: a row only re-renders when its own props change (its video object,
// selection state, or the stable callbacks). Ticking one checkbox or patching
// one row therefore re-renders that single row, not the whole 100+ row table.
export default React.memo(VideoRow);
